import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, CircularProgress } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import PatientDatas from "src/modules/medcard/patient-datas";
import { useAppSelector } from "src/app/hooks";
import { mcGeneralDiseaseService, McGeneralDiseaseItem } from "src/api/medcards/mcGeneralDiseaseService";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import { useTranslate } from "src/locales";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { parseISO } from "date-fns";
import { toLocalISO } from "src/utils/date/dateHelper";

interface DrugsModalProps {
  open: boolean;
  onClose: () => void;
  patientId?: string | number;
  editData?: McGeneralDiseaseItem | null;
}

const DrugsModal: React.FC<DrugsModalProps> = ({
  open,
  onClose,
  patientId,
  editData,
}) => {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => (state as any).auth);

  const [operationDate, setOperationDate] = useState<Date | null>(new Date());
  const [operationUser, setOperationUser] = useState<string>("");
  const [drugId, setDrugId] = useState<number | null>(null);
  const [drugName, setDrugName] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [dosePeriod, setDosePeriod] = useState<string>("");
  const [durationYear, setDurationYear] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [loading, setLoading] = useState(false);

  // Kullanıcı adını otomatik olarak login durumundan al
  useEffect(() => {
    if (user?.username) {
      setOperationUser(user.username);
    }
  }, [user]);

  // Edit data varsa form'u doldur
  useEffect(() => {
    if (open && editData) {
      setDrugId(editData.pid || null);
      setDrugName(editData.manualdrugname || editData.name || "");
      setSelectedItem({
        id: editData.pid,
        name: editData.manualdrugname || editData.name,
        displayField: editData.manualdrugname || editData.name,
      });
      setDosePeriod(editData.dose || "");
      setDurationYear(editData.usagetime?.toString() || "");
      setNote(editData.note || editData.privatenotes || "");
      if (editData.cuser) {
        setOperationUser(editData.cuser);
      }
      if (editData.pdate || editData.ctime) {
        try {
          const dateString = editData.pdate || editData.ctime;
          const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
          setOperationDate(date);
        } catch (e) {
          setOperationDate(new Date());
        }
      }
    }
  }, [open, editData]);

  // Modal açıldığında snackbar'ı temizle
  useEffect(() => {
    if (open) {
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  // Modal kapandığında state'leri temizle
  useEffect(() => {
    if (!open) {
      setDrugId(null);
      setDrugName("");
      setSelectedItem(null);
      setDosePeriod("");
      setDurationYear("");
      setNote("");
      setOperationDate(new Date());
      setSnackbarOpen(false);
      setSnackbarMessage("");
    }
  }, [open]);

  const handleSave = async () => {
    if (!patientId) {
      setSnackbarMessage(t("drug.errors.patient-id-not-found"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    // Edit modunda güncelleme
    if (editData) {
      if (!editData.id) {
        setSnackbarMessage(t("drug.errors.id-not-found"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      if (!drugId) {
        setSnackbarMessage(t("drug.errors.drug-not-selected"));
        setSnackbarType("error");
        setSnackbarOpen(true);
        return;
      }

      setLoading(true);
      try {
        await mcGeneralDiseaseService.updateMedication({
          id: editData.id,
          processid: drugId,
          processdate: toLocalISO(operationDate),
          manualdrugname: drugName || "",
          usagetime: durationYear || "",
          dose: dosePeriod || "",
          note: note || "",
        });

        setSnackbarMessage(t("drug.success.save-success"));
        setSnackbarType("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error: any) {
        console.error("İlaç kullanımı güncelleme hatası:", error);
        const errorMessage = error?.response?.data?.error || error?.error || t("drug.errors.save-error");
        setSnackbarMessage(errorMessage);
        setSnackbarType("error");
        setSnackbarOpen(true);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Yeni kayıt
    if (!drugId) {
      setSnackbarMessage(t("drug.errors.drug-not-selected"));
      setSnackbarType("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ptid: Number(patientId),
        processid: drugId,
        processdate: toLocalISO(operationDate),
        manualdrugname: drugName || "",
        usagetime: durationYear || "",
        dose: dosePeriod || "",
        note: note || "",
      };

      await mcGeneralDiseaseService.addMedication(requestData);
      setSnackbarMessage(t("drug.success.save-success"));
      setSnackbarType("success");
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("İlaç kullanımı kaydetme hatası:", error);
      const errorMessage = error?.response?.data?.error || error?.error || t("drug.errors.save-error");
      setSnackbarMessage(errorMessage);
      setSnackbarType("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 1.5, sm: 2 },
          width: "100%",
          maxWidth: "100%",
        }}
      >
        {/* Başlık */}
        <Box
          sx={{
            width: "100%",
            backgroundColor: "#f4f7fe",
            borderBottom: "1px solid #e0e0e0",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "flex-start",
            py: 1.5,
            px: 2.5,
            mb: 2,
          }}
        >
          <ByzMuiTypography isTitle={true} sx={{ fontSize: 20, lineHeight: "22px", color: "#111827" }}>
            {t("drug.title")}
          </ByzMuiTypography>
        </Box>

        {/* Save / Close */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
            gap: 1,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={loading} />
          <Box sx={{ flex: 1 }} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Hasta bilgisi kartı (grafiksiz) */}
        <Box sx={{ mb: 2 }}>
          <PatientDatas patientId={patientId} isCreateMedCard noMargin />
        </Box>

        {/* Form alanları */}
        <Box
          sx={{
            mt: 1,
            width: { xs: "100%", md: "60%" },
          }}
        >
          {/* Bölüm başlığı - Kullandığı İlaç */}
          <Box
            sx={{
              backgroundColor: "#F4F7FE",
              borderBottom: "1px solid #E0E0E0",
              mb: 1.5,
              py: 1,
            }}
          >
            <ByzMuiTypography sx={{ fontSize: 16 }}>
              {t("drug.title")}
            </ByzMuiTypography>
          </Box>

          <Grid container spacing={0}>
            {/* İşlem Tarihi */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("drug.operation-date")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiDateTimePicker
                    value={operationDate}
                    onChange={(val) => setOperationDate(val as Date | null)}
                    width="100%"
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* İşlem Yapan Kullanıcı */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("drug.operation-user")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="İşlem Yapan Kullanıcı"
                    value={operationUser}
                    onChange={(e) => setOperationUser(e.target.value)}
                    fullWidth
                    cantChange
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Adı (select) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("drug.drug-name")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzSelectListPopupInput
                    label={t("buttons.chose") || "Seçiniz"}
                    placeholder={t("buttons.chose") || "Seçiniz"}
                    value={drugName}
                    selectedItem={selectedItem}
                    disabled={!!editData}
                    singleSelection={true}
                    onChange={(value: any) => {
                      // Tek seçim için direkt item'ı al
                      const item = value;
                      if (item) {
                        setSelectedItem(item);
                        setDrugId(item?.id || item?.ID || null);
                        setDrugName(item?.name || item?.NAME || "");
                      } else {
                        setSelectedItem(null);
                        setDrugId(null);
                        setDrugName("");
                      }
                    }}
                    url="/reference/api/McGeneralDisease/field-used-medicine"
                    method="GET"
                    dialogTitle={t("drug.dialog-title")}
                    displayField="name"
                    columns={[
                      { field: "id", headerName: "ID", width: 100 },
                      { field: "name", headerName: t("drug.drug-name"), flex: 1 },
                      { field: "barcode", headerName: "Barkod", width: 150 },
                      { field: "gsspayed", headerName: "GSS Ödemesi", width: 150 },
                    ]}
                    dialogShowOkButton={true}
                    rowID={patientId}
                    showPatientData={false}
                    fullWidth
                    sx={{ width: "100%" }}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Doz-Periyod */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("drug.dose-period")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("drug.dose-period")}
                    value={dosePeriod}
                    onChange={(e) => setDosePeriod(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Süre (Yıl) */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("drug.duration-year")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("drug.duration-year")}
                    value={durationYear}
                    onChange={(e) => {
                      // Sadece rakamları kabul et
                      const value = e.target.value.replace(/[^0-9]/g, "");
                      setDurationYear(value);
                    }}
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Not */}
            <Grid
              item
              xs={12}
              sx={{
                py: 1,
              }}
            >
              <Grid container alignItems="center" spacing={2}>
                <Grid
                  item
                  xs={12}
                  md={4}
                  sx={{
                    pl: 0,
                    borderBottom: "1px solid #E0E0E0",
                    py: 1,
                  }}
                >
                  <ByzMuiTypography marginLeft="30px" fontWeight={400}>
                    {t("drug.note")}
                  </ByzMuiTypography>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ByzMuiTextField
                    label="Giriniz"
                    placeholder={t("drug.note") || "Not"}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Loading Overlay */}
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} />
        </Box>
      )}

      {/* Snackbar */}
      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        title={snackbarType === "success" ? t("common.success") : t("common.error")}
        message={snackbarMessage}
        type={snackbarType}
      />
    </OpenComponentDialog>
  );
};

export default DrugsModal;
